import { createPersistedStore, buildFileFormData } from '@tasama/utils';
import type { BaseStoreState, UploadedFileBase } from '@tasama/utils';
import { i18n } from '@tasama/i18n';
import { validateNDAUploadForm } from '../schemas/nda-upload.schema';

/**
 * NDA Upload store using Zustand with store-utils
 * Manages upload state for NDA documents
 *
 * NOTE: This store only manages UI state for files.
 * API calls should be made via useNDAApi hook in components.
 */

// Extended in-store type: base fields + the actual File object for server upload
export interface NDAUploadedFile extends UploadedFileBase {
  file?: File | undefined;
}

interface NDAUploadState extends BaseStoreState {
  // File state
  uploadedFiles: NDAUploadedFile[];
  // Actions (included in state for proper typing)
  setUploadedFiles: (_files: NDAUploadedFile[]) => void;
  addFile: (_file: NDAUploadedFile) => void;
  updateFile: (_fileId: string, _updates: Partial<NDAUploadedFile>) => void;
  removeFile: (_fileId: string) => void;
  clearFiles: () => void;
  reset: () => void;
  setUploadError: (_error: string | null) => void;
  validateFiles: () => { success: boolean; error?: string };
  getFormData: () => FormData | null;
}

const initialFormData: Omit<NDAUploadState, keyof BaseStoreState> = {
  uploadedFiles: [],
  // Placeholder functions - will be overwritten by customActions
  setUploadedFiles: () => {},
  addFile: () => {},
  updateFile: () => {},
  removeFile: () => {},
  clearFiles: () => {},
  reset: () => {},
  setUploadError: () => {},
  validateFiles: () => ({ success: false }),
  getFormData: () => null,
};

export const useNDAUploadStore = createPersistedStore<NDAUploadState>(
  'nda-upload',
  initialFormData,
  (set, get) => ({
    // Actions
    setUploadedFiles: (files: NDAUploadedFile[]) => {
      set({ uploadedFiles: files } as Partial<NDAUploadState>);
    },

    addFile: (file: NDAUploadedFile) => {
      const { uploadedFiles } = get();
      set({
        uploadedFiles: [...uploadedFiles, file],
      } as Partial<NDAUploadState>);
    },

    updateFile: (fileId: string, updates: Partial<NDAUploadedFile>) => {
      const { uploadedFiles } = get();
      const updatedFiles = uploadedFiles.map((file: NDAUploadedFile) =>
        file.id === fileId ? { ...file, ...updates } : file
      );
      set({ uploadedFiles: updatedFiles } as Partial<NDAUploadState>);
    },

    removeFile: (fileId: string) => {
      const { uploadedFiles } = get();
      const filteredFiles = uploadedFiles.filter(
        (file: NDAUploadedFile) => file.id !== fileId
      );
      set({ uploadedFiles: filteredFiles } as Partial<NDAUploadState>);
    },

    clearFiles: () => {
      set({ uploadedFiles: [] } as Partial<NDAUploadState>);
    },

    reset: () => {
      set({
        uploadedFiles: [],
        loading: false,
        error: null,
      } as Partial<NDAUploadState>);
    },

    setUploadError: (error: string | null) => {
      set({ error } as Partial<NDAUploadState>);
    },

    validateFiles: () => {
      const { uploadedFiles } = get();

      if (uploadedFiles.length === 0) {
        return {
          success: false,
          error: i18n.t('nda.validation.required', { ns: 'portal' }),
        };
      }

      const validationResult = validateNDAUploadForm({ uploadedFiles });

      if (!validationResult.success && validationResult.errors) {
        const firstError = Object.values(validationResult.errors)[0];
        return {
          success: false,
          error:
            firstError || i18n.t('errors.validationFailed', { ns: 'portal' }),
        };
      }

      return { success: true };
    },

    getFormData: () => {
      const { uploadedFiles } = get();
      const uploadFile = uploadedFiles[0];

      if (!uploadFile?.file) {
        return null;
      }

      return buildFileFormData(uploadFile.file, { uploadedBy: 'user' });
    },
  }),
  {
    partialize: (state) => ({
      // Don't persist File objects — strip to serializable fields only
      uploadedFiles: state.uploadedFiles.map((file: NDAUploadedFile) => ({
        id: file.id,
        fileName: file.fileName,
        fileSize: file.fileSize,
        bytes: file.bytes,
        progress: file.progress,
        status: file.status,
        // Exclude the actual File object from persistence
        file: undefined,
      })),
    }),
    clearOnRefresh: ['uploadedFiles'],
  }
);

// Selectors
export const useNDAUploadFiles = () =>
  useNDAUploadStore((state) => state.uploadedFiles);
export const useNDAUploadIsUploading = () =>
  useNDAUploadStore((state) => state.loading);
export const useNDAUploadError = () =>
  useNDAUploadStore((state) => state.error);
